/**
 * Modal shown when a transcript upload is rejected for exceeding the
 * per-file word cap.
 *
 * Two paths fire this modal:
 *
 *   1. Client-side pre-check on `.txt` files (in FileUpload.onDrop),
 *      before we even generate the upload URLs. Fast, saves bandwidth.
 *
 *   2. Server-side 413 from customer-api's `save_and_transcript_generate`
 *      after upload commit, for `.pdf` / `.doc` / `.docx` where the
 *      word count needs the text extraction pipeline.
 *
 * Pure helpers (constants, isTranscriptWordLimitError,
 * extractTranscriptWordLimitDetail, countWordsInText) live in
 * ``transcriptWordLimit.js`` alongside so react-refresh's "only export
 * components" rule stays green and the helpers can be unit-tested with
 * ``node --test`` (no vitest / jsdom needed for the pure JS).
 */

import { MailOutlineRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { TRANSCRIPT_WORD_LIMIT, TRANSCRIPT_WORD_LIMIT_CONTACT_URL } from './transcriptWordLimit';

const formatCount = (n) => (typeof n === 'number' ? n.toLocaleString() : null);

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {number}   [props.limit]              default 100k
 * @param {Array<{filename: string, wordCount: number|null}>} [props.overLimitFiles]
 * @param {string}   [props.contactUrl]         default qualz.ai/contactus
 * @param {()=>void} props.onClose
 */
const TranscriptWordLimitModal = ({
  open,
  limit = TRANSCRIPT_WORD_LIMIT,
  overLimitFiles = [],
  contactUrl = TRANSCRIPT_WORD_LIMIT_CONTACT_URL,
  onClose,
}) => {
  const limitLabel = formatCount(limit) || '100,000';
  const isSingle = overLimitFiles.length === 1;
  const subject = `Transcript upload exceeds ${limitLabel}-word per-file limit`;
  const mailto = contactUrl.startsWith('mailto:')
    ? contactUrl
    : `${contactUrl}?subject=${encodeURIComponent(subject)}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isSingle ? 'This file is too large to process' : 'Some files are too large to process'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.primary">
            Transcript uploads are capped at <strong>{limitLabel} words</strong> per file.{' '}
            {isSingle
              ? "The file below is over that limit and can't be processed here."
              : "The files below are over that limit and can't be processed here."}
          </Typography>
          {overLimitFiles.length > 0 && (
            <Box
              sx={{
                mt: 0.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                background: 'rgba(0,0,0,0.03)',
                fontSize: 13,
                color: 'text.secondary',
                fontFamily: 'monospace',
                maxHeight: 160,
                overflowY: 'auto',
              }}
            >
              {overLimitFiles.map((f, i) => (
                <Box key={`${f.filename}-${i}`}>
                  {f.filename}
                  {f.wordCount ? ` — ${formatCount(f.wordCount)} words` : ''}
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            Need to process larger files? Reach out and we&apos;ll help you set that up.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<MailOutlineRounded />}
          href={mailto}
          target={contactUrl.startsWith('mailto:') ? undefined : '_blank'}
          rel="noopener noreferrer"
        >
          Contact us
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranscriptWordLimitModal;
